import { useAppTranslation } from '../i18n/useAppTranslation';
import { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Lock, Schedule } from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { isNativeApp } from '../utils/platform';

/**
 * Bandeau d'abonnement pour les coachs.
 *
 * S'appuie sur GET /payments/subscriptions/access_status/ (source de verite
 * unique) :
 *   - state 'trial' et J-30 ou moins : rappel doux avec compte a rebours ;
 *   - state 'locked' : bandeau bloquant (essai termine, activation requise) ;
 *   - 'active' / 'unlimited' / essai large : rien.
 *
 * L'activation ouvre le paiement Stripe (prelevement immediat, sans second
 * essai — l'essai gratuit a deja couru depuis l'inscription).
 */
const TrialBanner = () => {
  const { tr } = useAppTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState<any>(null);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (user?.role !== 'COACH') return;
    api.get('/payments/subscriptions/access_status/')
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [user]);

  // Dans l'app native (iOS/Android), on n'affiche aucun parcours d'abonnement
  // Stripe (regles des stores). L'abonnement se souscrit depuis le site web.
  if (isNativeApp()) return null;

  if (!status) return null;

  const locked = status.state === 'locked';
  const daysLeft = status.trial_days_left;
  const showTrial = status.state === 'trial' && daysLeft !== null && daysLeft <= 30;

  if (!locked && !showTrial) return null;

  const activer = async () => {
    setActivating(true);
    try {
      const r = await api.post('/payments/subscriptions/create_checkout_session/');
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch {
      setActivating(false);
    }
  };

  const rouge = locked;

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        px: { xs: 2, sm: 3 },
        py: 1.5,
        mb: 2,
        borderRadius: 3,
        color: '#fff',
        background: rouge
          ? 'linear-gradient(90deg, #B91C1C 0%, #EF4444 100%)'
          : 'linear-gradient(90deg, #B45309 0%, #F59E0B 100%)',
        boxShadow: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        {rouge ? <Lock /> : <Schedule />}
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {rouge
            ? tr("Votre période d'essai gratuite est terminée. Activez votre abonnement Coach Pro pour continuer à créer sur la plateforme.")
            : `Il vous reste ${daysLeft} jour${daysLeft > 1 ? 's' : ''} d'essai gratuit. Pensez à activer votre abonnement pour ne pas être interrompu.`}
        </Typography>
      </Box>
      <Button
        variant="contained"
        onClick={activer}
        disabled={activating}
        startIcon={activating ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{
          bgcolor: '#fff',
          color: rouge ? '#B91C1C' : '#B45309',
          fontWeight: 800,
          borderRadius: 2,
          whiteSpace: 'nowrap',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
        }}
      >
         {tr("Activer — 19,90 €/mois")} </Button>
    </Box>
  );
};

export default TrialBanner;
